/**
 * A fileset's plan in the tab's checkpoint vm. The engine serves the plan of the fileset's current
 * revision (checkpointengine.v1.GetPlan) to a caller who may read the fileset; the tab keeps the plan
 * it holds per fileset and sends its hash, so a plan that has not changed is not sent again.
 * FEATURE-20261029-wasm-checkpoint-interpreter.
 */
import GlobalClient from "../hermesclient/client";
import { ContentType, RpcFrameType } from "../hermesclient/proto/wsmessages";
import { nameMappingService } from "../services/NameMappingService";
import { asLogicalMailbox } from "../hermesclient/types";
import { checkPlanFormat, CheckpointVmError, loadCheckpointVm, type CheckpointVm } from "./checkpoint-vm";

/** checkpointengine.v1.GetPlan's answer, as the JSON content type carries it (bytes as base64). */
export interface GetPlanAnswer {
  ok: boolean;
  planHash: string;
  plan: Uint8Array; // empty when planHash is the hash the caller sent
  planFormatVersion: number;
  revisionUid: string;
  errors: Array<{ filename?: string; message?: string }>;
}

export type GetPlan = (filesetUid: string, havePlanHash: string) => Promise<GetPlanAnswer>;

function fromBase64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** GetPlan over the tab's hermes connection. */
export const getPlanRpc: GetPlan = async (filesetUid, havePlanHash) => {
  const endPoint = "checkpointengine.v1.GetPlan";
  const rpcResponse = await GlobalClient.get().rawRpcCall({
    to: await nameMappingService.resolve(asLogicalMailbox("continuum")),
    endPoint,
    body: new TextEncoder().encode(JSON.stringify({ filesetUid, havePlanHash })),
    contentType: ContentType.Json,
  });
  if (rpcResponse.header?.rpcHeader?.frameType === RpcFrameType.ErrorResponse) {
    const errorInfo = rpcResponse.header?.rpcHeader?.errorInfo;
    throw new Error(errorInfo?.message || errorInfo?.errorNode?.frame?.message || `${endPoint} failed`);
  }
  const raw = JSON.parse(new TextDecoder().decode(rpcResponse.data)) as Record<string, unknown>;
  return {
    ok: raw["ok"] === true,
    planHash: String(raw["planHash"] ?? raw["plan_hash"] ?? ""),
    plan: typeof raw["plan"] === "string" ? fromBase64(raw["plan"]) : new Uint8Array(),
    planFormatVersion: Number(raw["planFormatVersion"] ?? raw["plan_format_version"] ?? 0),
    revisionUid: String(raw["revisionUid"] ?? raw["revision_uid"] ?? ""),
    errors: Array.isArray(raw["errors"]) ? (raw["errors"] as GetPlanAnswer["errors"]) : [],
  };
};

/** A fileset's plan, loaded in the vm: the handle every vm call takes, and the revision it is. */
export interface LoadedPlan {
  vm: CheckpointVm;
  handle: string;
  revisionUid: string;
}

const held = new Map<string, string>(); // fileset uid → the plan hash the vm holds for it

/** The fileset's current plan in the tab's vm, fetched only when it is not the one already held. */
export async function planOf(filesetUid: string, getPlan: GetPlan = getPlanRpc, vm?: CheckpointVm): Promise<LoadedPlan> {
  const theVm = vm ?? (await loadCheckpointVm());
  const have = held.get(filesetUid) ?? "";
  const got = await getPlan(filesetUid, have);
  if (!got.ok) {
    const first = got.errors[0];
    throw new CheckpointVmError(`the fileset does not compile${first ? `: ${first.filename ?? ""}: ${first.message ?? ""}` : ""}`);
  }
  if (got.planHash === have && have !== "") return { vm: theVm, handle: have, revisionUid: got.revisionUid };
  checkPlanFormat(theVm, got.planFormatVersion);
  const handle = theVm.load(got.plan);
  held.set(filesetUid, handle);
  return { vm: theVm, handle, revisionUid: got.revisionUid };
}
